import { useState } from "react";
import { usePatientsStore } from "@/lib/stores/patients";
import { useDoctorsStore } from "@/lib/stores/doctors";
import { Button } from "@/components/ui/button";
import { Clock, CheckCircle2, Stethoscope, Wallet } from "lucide-react";
import { PatientStatus, Patient } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { safeFormatTime, safeFormatDateOnly } from '@/lib/utils/dateUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface PatientQueueProps {
  status: PatientStatus | "all";
  filteredPatients?: Patient[];
}

export function PatientQueue({ status, filteredPatients }: PatientQueueProps) {
  const allPatients = usePatientsStore((state) => state.patients);
  const updatePatientStatus = usePatientsStore((state) => state.updatePatientStatus);
  const updatePatientDoctor = usePatientsStore((state) => state.updatePatientDoctor);
  const { doctors } = useDoctorsStore();
  const { toast } = useToast();
  const [selectedDoctorForPatient, setSelectedDoctorForPatient] = useState<Record<string, string>>({});
  const [assignDialogOpen, setAssignDialogOpen] = useState<Record<string, boolean>>({});
  
  // Use filtered patients if provided, otherwise use all patients
  const patients = filteredPatients || allPatients;
  // For staff dashboard, show ALL statuses in one table when status is "all"
  const displayPatients = status === "all" ? patients : patients.filter((patient) => patient.status === status);
  


  const getStatusIcon = (status: PatientStatus) => {
    switch (status) {
      case "waiting":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "in-consultation":
        return <Stethoscope className="h-4 w-4 text-blue-500" />;
      case "completed":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      default:
        return null;
    }
  };

  const getEmptyMessage = () => {
    switch (status) {
      case "waiting":
        return "No patients waiting";
      case "in-consultation":
        return "No patients in consultation";
      case "completed":
        return "No completed consultations";
      default:
        return "No patients";
    }
  };

  const getPriorityBadge = (priority: string) => {
    if (priority === "urgent") {
      return (
        <Badge variant="outline" className="bg-hospital-warning/10 text-hospital-warning border-hospital-warning text-xs">
          Urgent
        </Badge>
      );
    }
    if (priority === "emergency") {
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300 text-xs font-semibold">
          Emergency
        </Badge>
      );
    }
    return null;
  };

  const getPaymentStatus = (patient: any) => {
    // Check if services exist and calculate total
    const hasServices = patient.services && Array.isArray(patient.services) && patient.services.length > 0;
    const servicesCost = hasServices 
      ? patient.services.reduce((total: number, service: any) => {
          const price = typeof service.price === 'number' ? service.price : parseFloat(service.price) || 0;
          return total + price;
        }, 0)
      : 0;
    const totalAmount = servicesCost;
    const amountPaid = patient.amountPaid || 0;
    
    // For completed patients with no services (bill generated), show "Bills Cleared"
    if (patient.status === 'completed' && totalAmount === 0 && !hasServices) {
      return (
        <span className="text-xs text-blue-600 font-medium">Bills Cleared</span>
      );
    }
    
    // If there are services, always show payment status
    if (hasServices && totalAmount > 0) {
      if (amountPaid >= totalAmount) {
        return (
          <span className="text-xs text-green-600 font-medium">Paid: ₹{amountPaid}</span>
        );
      } else {
        const due = totalAmount - amountPaid;
        return (
          <span className="text-xs text-red-600 font-medium">Due: ₹{due}</span>
        );
      }
    }
    
    // If services exist but total is 0 (free services)
    if (hasServices && totalAmount === 0) {
      return (
        <span className="text-xs text-gray-600 font-medium">Free</span>
      );
    }
    
    // If no services and no payment
    return <span className="text-xs text-gray-400">-</span>;
  };

  const formatRegisteredDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      // Format as "27 Jan 2025"
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (error) {
      return '-';
    }
  };

  const formatRegisteredTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return safeFormatTime(dateString);
    } catch (error) {
      return '-';
    }
  };

  const formatMobileNumber = (contactNumber: string | null | undefined): string => {
    if (!contactNumber) return '-';
    // Remove all non-digit characters (hyphens, spaces, plus signs, parentheses, etc.)
    let cleaned = contactNumber.replace(/[^\d]/g, '');
    
    // Remove country codes (common Indian formats: +91, 91, +9191)
    // Remove +91 or 91 from the start
    if (cleaned.startsWith('91') && cleaned.length >= 12) {
      cleaned = cleaned.substring(2);
    }
    // Handle cases where it might be +9191XXXXXXXXXX (double prefix)
    if (cleaned.startsWith('91') && cleaned.length >= 12) {
      cleaned = cleaned.substring(2);
    }
    
    return cleaned.trim() || '-';
  };

  const getStatusBadge = (patientStatus: PatientStatus) => {
    switch (patientStatus) {
      case "waiting":
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs whitespace-nowrap">
            Waiting
          </Badge>
        );
      case "in-consultation":
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs whitespace-nowrap">
            In Consultation
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs whitespace-nowrap">
            Completed
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleAssignDoctor = (patientId: string) => {
    const doctorId = selectedDoctorForPatient[patientId];
    if (!doctorId || doctorId === "no_doctors_available") {
      toast({
        title: "Error",
        description: "Please select a doctor",
        variant: "destructive"
      });
      return;
    }

    try {
      updatePatientDoctor(patientId, doctorId);
      updatePatientStatus(patientId, "in-consultation", doctorId);
      setAssignDialogOpen(prev => ({ ...prev, [patientId]: false }));
      setSelectedDoctorForPatient(prev => ({ ...prev, [patientId]: "" }));
      toast({
        title: "Success",
        description: "Doctor assigned successfully"
      });
    } catch (error) {
      console.error("Error assigning doctor:", error);
      toast({
        title: "Error",
        description: "Failed to assign doctor",
        variant: "destructive"
      });
    }
  };

  if (displayPatients.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        No patients found
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Mobile: Horizontal scroll container */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <Table className="table-fixed w-full min-w-[800px]">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[15%] px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-xs sm:text-sm">Patient</TableHead>
                <TableHead className="w-[12%] px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-xs sm:text-sm">Time Registered</TableHead>
                <TableHead className="w-[12%] px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-xs sm:text-sm hidden md:table-cell">Mobile Number</TableHead>
                <TableHead className="w-[14%] px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-xs sm:text-sm hidden lg:table-cell">Location</TableHead>
                <TableHead className="w-[16%] px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-xs sm:text-sm hidden lg:table-cell">Reason</TableHead>
                <TableHead className="w-[11%] px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-xs sm:text-sm">Status</TableHead>
                <TableHead className="w-[10%] px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-xs sm:text-sm hidden sm:table-cell">Payment</TableHead>
                <TableHead className="w-[10%] px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-xs sm:text-sm">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayPatients.map((patient) => (
                <TableRow key={patient.id} className="hover:bg-gray-50">
                  <TableCell className="px-2 sm:px-4 py-2 sm:py-3 text-left">
                    <div>
                      <Link 
                        to={`/patient/${patient.id}`} 
                        className="hover:text-hospital-primary transition-colors font-semibold text-gray-900 block text-xs sm:text-sm"
                      >
                        {patient.name}
                      </Link>
                      <span className="text-xs text-gray-600">
                        {patient.age} / {patient.gender}
                      </span>
                      {/* Mobile: Show mobile number and location below name */}
                      <div className="md:hidden mt-1 space-y-0.5">
                        <span className="text-xs text-gray-500">
                          📱 {formatMobileNumber(patient.contactNumber)}
                        </span>
                        {patient.location && (
                          <span className="text-xs text-gray-500 block">
                            📍 {patient.location}
                          </span>
                        )}
                        {patient.reason && (
                          <span className="text-xs text-gray-500 block">
                            💬 {patient.reason}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="px-2 sm:px-4 py-2 sm:py-3 text-left">
                    <div className="flex flex-col">
                      <span className="text-xs sm:text-sm text-gray-700 font-medium">
                        {formatRegisteredTime(patient.registeredAt)}
                      </span>
                      <span className="text-xs text-gray-600">
                        {formatRegisteredDate(patient.registeredAt)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="px-2 sm:px-4 py-2 sm:py-3 text-left hidden md:table-cell">
                    <span className="text-xs sm:text-sm text-gray-700">
                      {formatMobileNumber(patient.contactNumber)}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-4 py-2 sm:py-3 text-left hidden lg:table-cell">
                    <span className="text-xs sm:text-sm text-gray-700">
                      {patient.location || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-4 py-2 sm:py-3 text-left hidden lg:table-cell">
                    <span className="text-xs sm:text-sm text-gray-700">
                      {patient.reason || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-4 py-2 sm:py-3 text-left">
                    {getStatusBadge(patient.status)}
                  </TableCell>
                  <TableCell className="px-2 sm:px-4 py-2 sm:py-3 text-left hidden sm:table-cell">
                    {getPaymentStatus(patient)}
                  </TableCell>
                  <TableCell className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                    {patient.status === "waiting" ? (
                      <Dialog open={assignDialogOpen[patient.id] || false} onOpenChange={(open) => setAssignDialogOpen(prev => ({ ...prev, [patient.id]: open }))}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="border-hospital-primary text-hospital-primary hover:bg-hospital-primary/10 text-xs px-2 sm:px-3">
                            <span className="hidden sm:inline">Assign</span>
                            <span className="sm:hidden">A</span>
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Assign Doctor</DialogTitle>
                            <DialogDescription>
                              Select a doctor to assign to {patient.name}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="py-4">
                            <Select 
                              value={selectedDoctorForPatient[patient.id] || ""} 
                              onValueChange={(value) => setSelectedDoctorForPatient(prev => ({ ...prev, [patient.id]: value }))}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select a doctor" />
                              </SelectTrigger>
                              <SelectContent>
                                {doctors.length > 0 ? (
                                  doctors.map((doctor) => (
                                    <SelectItem key={doctor.id} value={doctor.id}>
                                      {doctor.name} - {doctor.specialization}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="no_doctors_available" disabled>
                                    No doctors available
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                            <Button 
                              className="mt-4 w-full"
                              onClick={() => handleAssignDoctor(patient.id)}
                            >
                              Assign Doctor
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    ) : patient.status === "in-consultation" ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-hospital-success text-hospital-success hover:bg-hospital-success/10 text-xs px-2 sm:px-3"
                        onClick={() => updatePatientStatus(patient.id, "completed")}
                      >
                        <span className="hidden sm:inline">Complete</span>
                        <span className="sm:hidden">✓</span>
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
} 